import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/pages/layouts/AdminLayout";
import { supabase } from "@/shared/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table-primitives";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shared/components/ui/context-menu";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Loader2, ChevronDown, Plus, Edit, Trash } from "lucide-react";
import { format } from "date-fns";

interface PixelEmailContent {
  id: number;
  page_url: string;
  subject_1: string | null;
  headline_1: string | null;
  checkmark_1: string | null;
  subject_2: string | null;
  headline_2: string | null;
  intro1_2: string | null;
  intro2_2: string | null;
  body_3: string | null;
  created_at: string;
  updated_at: string;
}

type SortOrder = "asc" | "desc";

interface FormData {
  page_url: string;
  subject_1: string;
  headline_1: string;
  checkmark_1: string;
  subject_2: string;
  headline_2: string;
  intro1_2: string;
  intro2_2: string;
  body_3: string;
}

export default function PixelContent() {
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContent, setSelectedContent] = useState<PixelEmailContent | null>(null);
  const [formData, setFormData] = useState<FormData>({
    page_url: "",
    subject_1: "",
    headline_1: "",
    checkmark_1: "",
    subject_2: "",
    headline_2: "",
    intro1_2: "",
    intro2_2: "",
    body_3: "",
  });

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["pixel-email-content", sortOrder],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/communications/pixel-content?sort=${sortOrder}`,
        {
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch pixel content");
      }

      const data = await response.json();
      return data as PixelEmailContent[];
    },
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/communications/pixel-content`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add content");
      }

      // Reset form and close modal
      setFormData({
        page_url: "",
        subject_1: "",
        headline_1: "",
        checkmark_1: "",
        subject_2: "",
        headline_2: "",
        intro1_2: "",
        intro2_2: "",
        body_3: "",
      });
      setIsAddModalOpen(false);

      // Refetch data
      queryClient.invalidateQueries({ queryKey: ["pixel-email-content"] });
    } catch (error) {
      console.error("Error adding content:", error);
      alert(error instanceof Error ? error.message : "Failed to add content");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContent) return;
    setIsSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/communications/pixel-content/${
          selectedContent.id
        }`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update content");
      }

      // Reset form and close modal
      setFormData({
        page_url: "",
        subject_1: "",
        headline_1: "",
        checkmark_1: "",
        subject_2: "",
        headline_2: "",
        intro1_2: "",
        intro2_2: "",
        body_3: "",
      });
      setIsEditModalOpen(false);
      setSelectedContent(null);

      // Refetch data
      queryClient.invalidateQueries({ queryKey: ["pixel-email-content"] });
    } catch (error) {
      console.error("Error updating content:", error);
      alert(error instanceof Error ? error.message : "Failed to update content");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedContent) return;
    setIsSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/communications/pixel-content/${
          selectedContent.id
        }`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete content");
      }

      setIsDeleteModalOpen(false);
      setSelectedContent(null);

      // Refetch data
      queryClient.invalidateQueries({ queryKey: ["pixel-email-content"] });
    } catch (error) {
      console.error("Error deleting content:", error);
      alert(error instanceof Error ? error.message : "Failed to delete content");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (content: PixelEmailContent) => {
    setSelectedContent(content);
    setFormData({
      page_url: content.page_url,
      subject_1: content.subject_1 || "",
      headline_1: content.headline_1 || "",
      checkmark_1: content.checkmark_1 || "",
      subject_2: content.subject_2 || "",
      headline_2: content.headline_2 || "",
      intro1_2: content.intro1_2 || "",
      intro2_2: content.intro2_2 || "",
      body_3: content.body_3 || "",
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (content: PixelEmailContent) => {
    setSelectedContent(content);
    setIsDeleteModalOpen(true);
  };

  return (
    <AdminLayout title="Pixel Email Content" maxWidth="full">
      <div className="space-y-6">
        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6 pl-3">
              <h2 className="text-lg font-medium text-slate-900">All Pixel Email Content</h2>
              <Button onClick={() => setIsAddModalOpen(true)} className="gap-2" size="sm">
                <Plus className="w-4 h-4" />
                Add Content
              </Button>
            </div>

            {/* Sort Dropdown */}
            <div className="mb-4 pl-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Sort by:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-2 text-sm">
                      Date Created ({sortOrder === "desc" ? "Newest First" : "Oldest First"})
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem onClick={() => setSortOrder("desc")} className="text-sm">
                      Date Created (Newest First)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSortOrder("asc")} className="text-sm">
                      Date Created (Oldest First)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-600 font-medium">Error loading content</div>
                <div className="text-sm text-slate-500 mt-2">
                  {error instanceof Error ? error.message : "Unknown error"}
                </div>
              </div>
            ) : data && data.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-slate-600 font-medium">No content found</div>
                <div className="text-sm text-slate-500 mt-2">
                  Add your first page content to get started.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200">
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Page URL
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Subject - 1
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Headline - 1
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Checkmark - 1
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Subject - 2
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Headline - 2
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Intro1 - 2
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Intro2 - 2
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Body - 3
                      </TableHead>
                      <TableHead className="text-sm font-semibold text-slate-600">
                        Created
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.map((content) => (
                      <ContextMenu key={content.id}>
                        <ContextMenuTrigger asChild>
                          <TableRow className="hover:bg-slate-50 transition-colors duration-200 border-slate-100 cursor-pointer">
                            <TableCell className="font-medium text-slate-900 min-w-[200px]">
                              {content.page_url}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.subject_1 ? (
                                <span className="line-clamp-2" title={content.subject_1}>
                                  {content.subject_1}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.headline_1 ? (
                                <span className="line-clamp-2" title={content.headline_1}>
                                  {content.headline_1}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.checkmark_1 ? (
                                <span className="line-clamp-2" title={content.checkmark_1}>
                                  {content.checkmark_1}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.subject_2 ? (
                                <span className="line-clamp-2" title={content.subject_2}>
                                  {content.subject_2}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.headline_2 ? (
                                <span className="line-clamp-2" title={content.headline_2}>
                                  {content.headline_2}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.intro1_2 ? (
                                <span className="line-clamp-2" title={content.intro1_2}>
                                  {content.intro1_2}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.intro2_2 ? (
                                <span className="line-clamp-2" title={content.intro2_2}>
                                  {content.intro2_2}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[200px]">
                              {content.body_3 ? (
                                <span className="line-clamp-2" title={content.body_3}>
                                  {content.body_3}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                              {format(new Date(content.created_at), "MMM d, yyyy")}
                            </TableCell>
                          </TableRow>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          <ContextMenuItem onClick={() => openEditModal(content)} className="gap-2">
                            <Edit className="w-4 h-4" />
                            Edit
                          </ContextMenuItem>
                          <ContextMenuItem
                            onClick={() => openDeleteModal(content)}
                            className="gap-2 text-red-600"
                          >
                            <Trash className="w-4 h-4" />
                            Delete
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Content Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleAdd}>
            <DialogHeader>
              <DialogTitle>Add New Pixel Email Content</DialogTitle>
              <DialogDescription>
                Add content for a new page URL. All fields are required.
              </DialogDescription>
            </DialogHeader>

            <DialogBody className="space-y-4 overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="page_url">
                  Page URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="page_url"
                  value={formData.page_url}
                  onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                  placeholder="/resources/your-page"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject_1">
                  Subject - 1 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subject_1"
                  value={formData.subject_1}
                  onChange={(e) => setFormData({ ...formData, subject_1: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="headline_1">
                  Headline - 1 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="headline_1"
                  value={formData.headline_1}
                  onChange={(e) => setFormData({ ...formData, headline_1: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="checkmark_1">
                  Checkmark - 1 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="checkmark_1"
                  value={formData.checkmark_1}
                  onChange={(e) => setFormData({ ...formData, checkmark_1: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject_2">
                  Subject - 2 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subject_2"
                  value={formData.subject_2}
                  onChange={(e) => setFormData({ ...formData, subject_2: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="headline_2">
                  Headline - 2 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="headline_2"
                  value={formData.headline_2}
                  onChange={(e) => setFormData({ ...formData, headline_2: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="intro1_2">
                  Intro1 - 2 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="intro1_2"
                  value={formData.intro1_2}
                  onChange={(e) => setFormData({ ...formData, intro1_2: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="intro2_2">
                  Intro2 - 2 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="intro2_2"
                  value={formData.intro2_2}
                  onChange={(e) => setFormData({ ...formData, intro2_2: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="body_3">
                  Body - 3 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="body_3"
                  value={formData.body_3}
                  onChange={(e) => setFormData({ ...formData, body_3: e.target.value })}
                  rows={4}
                  required
                />
              </div>
            </DialogBody>

            <div>
              <div className="flex gap-3 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Content"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Content Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Pixel Email Content</DialogTitle>
              <DialogDescription>
                Update content for this page URL. All fields are required.
              </DialogDescription>
            </DialogHeader>

            <DialogBody className="space-y-4 overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="edit_page_url">
                  Page URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_page_url"
                  value={formData.page_url}
                  onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                  placeholder="/resources/your-page"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_subject_1">
                  Subject - 1 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_subject_1"
                  value={formData.subject_1}
                  onChange={(e) => setFormData({ ...formData, subject_1: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_headline_1">
                  Headline - 1 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_headline_1"
                  value={formData.headline_1}
                  onChange={(e) => setFormData({ ...formData, headline_1: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_checkmark_1">
                  Checkmark - 1 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="edit_checkmark_1"
                  value={formData.checkmark_1}
                  onChange={(e) => setFormData({ ...formData, checkmark_1: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_subject_2">
                  Subject - 2 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_subject_2"
                  value={formData.subject_2}
                  onChange={(e) => setFormData({ ...formData, subject_2: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_headline_2">
                  Headline - 2 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_headline_2"
                  value={formData.headline_2}
                  onChange={(e) => setFormData({ ...formData, headline_2: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_intro1_2">
                  Intro1 - 2 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="edit_intro1_2"
                  value={formData.intro1_2}
                  onChange={(e) => setFormData({ ...formData, intro1_2: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_intro2_2">
                  Intro2 - 2 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="edit_intro2_2"
                  value={formData.intro2_2}
                  onChange={(e) => setFormData({ ...formData, intro2_2: e.target.value })}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_body_3">
                  Body - 3 <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="edit_body_3"
                  value={formData.body_3}
                  onChange={(e) => setFormData({ ...formData, body_3: e.target.value })}
                  rows={4}
                  required
                />
              </div>
            </DialogBody>

            <div>
              <div className="flex gap-3 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Content"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Pixel Email Content</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the content for{" "}
              <strong>{selectedContent?.page_url}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div>
            <div className="flex gap-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isSubmitting}
                variant="destructive"
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
